
import React, { useState, useMemo } from 'react';
import { BookingSplitRow, ServiceItem } from '../types';
import { Button } from './Button';
import { safeGetItem, safeSetItem } from '../services/safeStorage';
import { defaultHoursForService } from '../services/projectLv';
import { isHourlyUnit } from '../services/serviceUnits';
import { isNepService, NEP_BADGE } from '../services/nepPosition';
import { FLAT_PROOF_HINT } from '../services/flatRateLogic';
import {
    BillingRowInput,
    evaluateBillingRows,
    lastBillingStorageKey,
    pickInitialBillingIds,
    serializeLastBillingValue,
    toBookingRows,
    totalBookedQuantity,
} from '../services/billingSelection';
/* Fix: Added missing AlertTriangle import and removed unused AlertCircle */
import { Wallet, X, CheckCircle2, Clock, AlertTriangle, Info, Lock, Plus, Trash2, Layers } from 'lucide-react';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    /**
     * `serviceId`/`hours` sind die Werte der ERSTEN Zeile — jeder Aufrufer, der
     * nicht aufteilt, laeuft damit unveraendert weiter. `rows` traegt die
     * vollstaendige Aufteilung (bei einer Zeile genau diese eine, bei „ohne
     * Leistungsbuchung" die leere Liste).
     */
    onConfirm: (serviceId: string | null, hours: number, rows: BookingSplitRow[]) => void;
    services: ServiceItem[];
    /** Ueberschrift. Ohne Angabe entscheidet der Kontext, siehe CONTEXT_TEXTS. */
    title?: string;
    context?: string; // 'vorank' | 'unterlage' | 'sigeplan' | 'inspection' | 'einweisung' | 'gbu' | 'generic'
    blockedServices?: Record<string, { reason: string }>; // NEW: Blocked services map
    /**
     * Aufteilung auf mehrere LV-Positionen (Lose) anbieten. Standard aus: nur
     * der Begehungsablauf teilt auf. Wo der Aufrufer die Buchung nicht
     * zeilenweise verarbeiten kann (Abrechnung ueber ein Mutterprojekt), bleibt
     * der Knopf weg, statt dass stillschweigend nur die erste Zeile buchte.
     */
    allowSplit?: boolean;
    /**
     * Darf dieser Nutzer Geldbetraege sehen? Dieselbe Regel wie in ProjectHub
     * (`canSeeFinancials` = admin | super_admin) und in ProjectStats /
     * ServiceDetailsModal: der Innendienst sieht Werte, der Aussendienst
     * MENGEN. Dieser Dialog ist der einzige Ort, an dem ein Mitarbeiter das
     * vollstaendige LV vor sich hat — ohne diese Wache stuende dort die
     * komplette Preisliste des Kunden.
     *
     * Vorgabe FALSE, nicht true: eine vergessene Aufrufstelle soll Preise
     * verschweigen, nicht offenlegen.
     */
    canSeeFinancials?: boolean;
    /**
     * Projekt, fuer das die Vorauswahl gemerkt wird (`lastBillingStorageKey`).
     * Ohne Angabe gilt die gemerkte Position fuer jedes Projekt auf dem Geraet —
     * bei Mutter und Tochter mit demselben LV bucht dann die eine auf die
     * Position der anderen.
     */
    memoryScope?: string | null;
    /** Vorauswahl aus dem Protokoll des Projekts; schlaegt den Geraetespeicher. */
    preferredIds?: string[];
    /**
     * Ein Satz OBEN im Dialog, vor der Auswahl — etwa „Die Position … ist
     * bereits gebucht" bei der Freigabe einer neuen Fassung (Plan
     * Paketposition B5). Ohne Angabe bleibt der Dialog, wie er war.
     */
    vorabHinweis?: string | null;
    /**
     * Startet mit „Vorgang ohne Leistungsbuchung abschließen" statt mit einer
     * vorgewaehlten Position. Fuer Auswahlen aus ALLEN Positionen (Handbuch,
     * Plan B5): dort gibt es keine passende Position, und eine still
     * vorgewaehlte erste Zeile buchte mit einem Klick auf etwas Fremdes.
     */
    ohneVorauswahl?: boolean;
    /**
     * Vorbelegung des Mengenfeldes bei STUNDENpositionen — etwa die schon
     * erfasste Zeit aus „Zeit buchen" der Gefaehrdungsbeurteilung, damit sie
     * nicht ein zweites Mal getippt werden muss. Bleibt aenderbar; die
     * Mindeststunden-Warnung gilt weiter. Ohne Angabe (oder <= 0) wie bisher
     * die Vorgabe der Position. Stueckpositionen bleiben unberuehrt.
     */
    vorgabeMenge?: number;
    /**
     * Nichts vorauswaehlen — weder eine Position noch „ohne Leistungsbuchung":
     * der Nutzer waehlt bewusst, „Auswahl bestätigen" ist bis dahin gesperrt.
     * Fuer Angebote aus fremden Positionen (GBU-Zeit auf eine allgemeine
     * Stundenposition): eine still vorgewaehlte erste Zeile buchte mit einem
     * Klick auf etwas Fremdes, eine vorgewaehlte „ohne Buchung" liesse die
     * Zeit still aus Leistung & Budget fallen.
     */
    bewussteWahl?: boolean;
}

const DEFAULT_BLOCKED_SERVICES = {};
const DEFAULT_PREFERRED_IDS: string[] = [];

/**
 * Die wenigen Texte, die vom Vorgang abhaengen. Alles andere im Dialog ist
 * kontextfrei und bleibt es — die REGELN liegen in billingSelection.ts, hier
 * steht nur, wie der Dialog den Vorgang benennt.
 *
 * WICHTIG: ein Kontext OHNE Eintrag faellt auf die Standardtexte zurueck, und
 * die sind wortgleich die bisherigen. Vorankuendigung, Unterlage, SiGe-Plan und
 * Begehung laufen damit unveraendert weiter; nur die Einweisung sagt etwas
 * Eigenes, weil „Dauer der Begehung" dort schlicht falsch waere. Ebenso die
 * Gefaehrdungsbeurteilung (`gbu`, docs/sifa-gbu-plan.md §10): „Zeit buchen"
 * in der GBU bucht Stunden auf eine Stundenposition.
 */
const CONTEXT_TEXTS: Record<string, { title?: string; intro?: string; hoursMissing?: string }> = {
    einweisung: {
        title: 'Einweisung verbuchen',
        intro: 'Auf welche Position aus dem Leistungsverzeichnis soll diese Einweisung gebucht werden?',
        hoursMissing: 'Bitte geben Sie die Dauer der Einweisung an.',
    },
    gbu: {
        title: 'Zeitaufwand Gefährdungsbeurteilung',
        intro: 'Auf welche Position aus dem Leistungsverzeichnis soll der Zeitaufwand für diese Gefährdungsbeurteilung gebucht werden?',
        hoursMissing: 'Bitte geben Sie den Zeitaufwand für die Gefährdungsbeurteilung an.',
    },
};

const DEFAULT_TITLE = 'Leistung verbuchen';
const DEFAULT_INTRO = 'Welche Position aus dem Leistungsverzeichnis soll für diesen Vorgang gebucht werden?';
const DEFAULT_HOURS_MISSING = 'Bitte geben Sie die Dauer der Begehung an.';

// "1" liest sich in einem Stundenfeld wie eine Stueckzahl — ganze Stunden
// deshalb mit einer Nachkommastelle, gebrochene wortgetreu.
const formatHoursInput = (value: number): string =>
    Number.isInteger(value) ? value.toFixed(1) : String(value);

// Deutsche Anzeige der Mindeststunden (2 -> "2", 1.5 -> "1,5").
const formatHoursLabel = (value: number): string => value.toLocaleString('de-DE');

const formatEuro = (value: number): string =>
    value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

/**
 * Vollstaendiger Positionstext fuer den Hover. Die Zeile selbst zeigt den Titel
 * inzwischen umgebrochen statt abgeschnitten; der Tooltip ergaenzt, was aus
 * Platzgruenden nicht in der Zeile steht (Kunden-LV-Nummer, Einheitspreis,
 * Restmenge). Bewusst der native `title`-Tooltip: die Liste scrollt, ein
 * eingebauter Kasten wuerde am Rand des Scrollbereichs abgeschnitten.
 */
const serviceTooltip = (s: ServiceItem, blockedReason: string | undefined, takenElsewhere: boolean, canSeeFinancials: boolean): string => {
    const lines = [`${s.lvPosition} ${s.title}`.trim()];
    if (s.clientLvPosition && s.clientLvPosition !== s.lvPosition) {
        lines.push(`LV-Nr. des Kunden: ${s.clientLvPosition}`);
    }
    if (isNepService(s)) {
        // NEP: kein Soll, kein Rest — beauftragt ist nur das Gebuchte.
        lines.push(`${NEP_BADGE} – nicht beauftragt, gebucht: ${s.quantityUsed} ${s.unit} (Angebotsmenge ${s.quantityContracted} ${s.unit})`);
    } else {
        lines.push(`Gebucht: ${s.quantityUsed} von ${s.quantityContracted} ${s.unit}`);
        const remaining = s.quantityContracted - s.quantityUsed;
        if (s.quantityContracted > 0) {
            lines.push(remaining > 0 ? `Rest: ${remaining} ${s.unit}` : 'Soll erreicht');
        }
    }
    if (s.unitPrice && canSeeFinancials) lines.push(`Einheitspreis: ${formatEuro(s.unitPrice)} / ${s.unit}`);
    if (s.minHours) lines.push(`Mindestens ${formatHoursLabel(s.minHours)} Std. je Buchung`);
    if (takenElsewhere) lines.push('Bereits in einer anderen Zeile gewählt');
    if (blockedReason) lines.push(`Gesperrt: ${blockedReason}`);
    return lines.join('\n');
};

interface RowState extends BillingRowInput {
    /** Stabiler React-Key: die Position darf sich aendern, die Zeile bleibt dieselbe. */
    key: string;
}

let rowKeySeq = 0;
const makeRow = (serviceId: string | null, services: ServiceItem[], vorgabeMenge?: number): RowState => {
    const svc = services.find(s => s.id === serviceId);
    // Die Vorgabe des Aufrufers nur bei einer Stundenposition und nur als echte Zahl > 0.
    const vorgabe = svc && isHourlyUnit(svc.unit) && typeof vorgabeMenge === 'number' && Number.isFinite(vorgabeMenge) && vorgabeMenge > 0
        ? vorgabeMenge
        : null;
    return {
        key: `billing-row-${++rowKeySeq}`,
        serviceId,
        amount: formatHoursInput(vorgabe ?? defaultHoursForService(svc || {})),
    };
};

export const BillingSelectionModal: React.FC<Props> = ({ isOpen, onClose, onConfirm, services, title, context = 'generic', blockedServices = DEFAULT_BLOCKED_SERVICES, allowSplit = false, canSeeFinancials = false, memoryScope = null, preferredIds = DEFAULT_PREFERRED_IDS, vorabHinweis = null, ohneVorauswahl = false, vorgabeMenge, bewussteWahl = false }) => {
    // Ein vom Aufrufer gesetzter Titel schlaegt den Kontext — jeder bestehende
    // Aufruf behaelt damit genau seine Ueberschrift.
    const texts = CONTEXT_TEXTS[context] || {};
    const heading = title || texts.title || DEFAULT_TITLE;
    // Der Dialog wird nur gemountet, solange er offen ist – die Vorauswahl ist deshalb Initial-State.
    // Vorbelegung der Menge aus der GEWAEHLTEN Position statt der festen 1.0: wer
    // eine Mindeststundenzahl vereinbart hat, soll sie nicht bei jeder Buchung
    // von Hand nachtragen muessen (unkorrigiert wurde bisher zu wenig gebucht).
    const [rows, setRows] = useState<RowState[]>(() =>
        (ohneVorauswahl || bewussteWahl
            ? [null]
            : pickInitialBillingIds(services, context, blockedServices, safeGetItem(lastBillingStorageKey(context, memoryScope)), allowSplit, preferredIds))
            .map(id => makeRow(id, services, vorgabeMenge)));

    const evaluations = useMemo(() => evaluateBillingRows(rows, services), [rows, services]);
    // `bewussteWahl`: erst eine Position oder „ohne Leistungsbuchung" anklicken, dann bestätigen.
    const [gewaehlt, setGewaehlt] = useState(!bewussteWahl);

    // Auswahl IMMER hierueber wechseln: der useState-Initialwert oben laeuft
    // nur einmal, ohne diese Stelle stuende nach einem Positionswechsel weiter
    // der Vorschlag der vorigen Position im Feld. Ein selbst eingetragener Wert
    // bleibt erhalten, solange dieselbe Position gewaehlt bleibt.
    const chooseService = (index: number, id: string | null) => {
        setGewaehlt(true);
        setRows(prev => prev.map((r, i) =>
            i !== index || r.serviceId === id ? r : { ...makeRow(id, services, vorgabeMenge), key: r.key }));
    };

    const setAmount = (index: number, raw: string) => {
        setRows(prev => prev.map((r, i) => (i === index ? { ...r, amount: raw } : r)));
    };

    const addRow = () => {
        setRows(prev => {
            const used = new Set(prev.map(r => r.serviceId).filter((v): v is string => !!v));
            const next = services.find(s => !blockedServices[s.id] && !used.has(s.id));
            return [...prev, makeRow(next ? next.id : null, services)];
        });
    };

    const removeRow = (index: number) => {
        setRows(prev => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
    };

    if (!isOpen) return null;

    const isSplit = rows.length > 1;
    const usedIds = new Set(rows.map(r => r.serviceId).filter((v): v is string => !!v));
    const hasFreeService = services.some(s => !blockedServices[s.id] && !usedIds.has(s.id));
    // Bestaetigen sperrt nur bei echter Fehleingabe. Ein Unterschreiten der
    // Mindeststunden bleibt ausdruecklich erlaubt (weiche Regel, nur Warnung).
    const isValid = evaluations.every(e => e.valid);
    const total = totalBookedQuantity(evaluations);

    const handleConfirm = () => {
        const bookingRows = toBookingRows(evaluations);
        const stored = serializeLastBillingValue(bookingRows.map(r => r.serviceId));
        if (stored !== null) {
            safeSetItem(lastBillingStorageKey(context, memoryScope), stored);
        }
        onConfirm(bookingRows[0]?.serviceId ?? null, bookingRows[0]?.quantity ?? 1, bookingRows);
    };

    const confirmBlocked = (blockInfo: { reason: string }, onForce: () => void) => {
        if (confirm(`ACHTUNG: ${blockInfo.reason}\n\nMöchten Sie die Abrechnung für diesen Tag trotzdem erzwingen? Dies kann zu doppelten Buchungen führen.`)) {
            onForce();
        }
    };

    return (
        <div data-tutorial="billing.auswahl" className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            {/*
              * Breite folgt dem Inhalt: eine einzelne Buchung braucht wenig,
              * die Aufteilung auf mehrere Lose zeigt lange LV-Titel und darf
              * dafuer die volle Dialogbreite nutzen.
              */}
            <div className={`bg-white rounded-2xl shadow-2xl w-full ${isSplit ? 'max-w-3xl' : 'max-w-xl'} overflow-hidden animate-scale-in flex flex-col max-h-[92vh]`}>
                <div className="bg-slate-900 text-white p-5 flex justify-between items-center shrink-0">
                    <h3 className="font-bold text-lg flex items-center gap-2">
                        <Wallet className="w-5 h-5 text-blue-400" />
                        {heading}
                    </h3>
                    <button onClick={onClose} className="hover:bg-white/10 p-1 rounded transition-colors">
                        <X className="w-5 h-5"/>
                    </button>
                </div>

                <div className="p-6 space-y-6 flex-1 min-h-0 overflow-y-auto custom-scrollbar">
                    {vorabHinweis && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                            <p className="text-[12px] text-amber-900 font-bold leading-snug">{vorabHinweis}</p>
                        </div>
                    )}
                    {!gewaehlt && (
                        <p className="text-[12px] text-blue-800 bg-blue-50 border border-blue-200 rounded-xl p-3 font-bold leading-snug">
                            Bitte eine Position wählen — oder bewusst „ohne Leistungsbuchung".
                        </p>
                    )}
                    {rows.map((row, index) => {
                        const ev = evaluations[index];
                        const rowService = services.find(s => s.id === row.serviceId);

                        return (
                            <div
                                key={row.key}
                                className={isSplit ? 'rounded-xl border-2 border-slate-200 p-3 space-y-4' : 'space-y-6'}
                            >
                                {isSplit && (
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-[11px] font-black uppercase tracking-wide text-slate-500">
                                            Los / Position {index + 1}
                                        </span>
                                        <button
                                            onClick={() => removeRow(index)}
                                            className="text-[11px] font-bold text-slate-400 hover:text-red-600 flex items-center gap-1 transition-colors"
                                        >
                                            <Trash2 className="w-3 h-3" /> Zeile entfernen
                                        </button>
                                    </div>
                                )}

                                <div>
                                    {!isSplit && (
                                        <p className="text-sm text-slate-600 mb-4">
                                            {texts.intro || DEFAULT_INTRO}
                                        </p>
                                    )}

                                    <div className={`space-y-2 ${isSplit ? 'max-h-72' : 'max-h-96'} overflow-y-auto pr-1 custom-scrollbar`}>
                                        {services.map(s => {
                                            const isSelected = row.serviceId === s.id;
                                            // NEP hat kein Soll, ist also nie „erreicht".
                                            const isDone = !isNepService(s) && s.quantityContracted > 0 && s.quantityUsed >= s.quantityContracted;

                                            const blockInfo = blockedServices[s.id];
                                            const isBlocked = !!blockInfo;
                                            // Dieselbe Position zweimal zu buchen ist immer ein Fehler
                                            // (die zweite Buchung liefe in den Idempotenz-Riegel).
                                            // Sie wird deshalb gar nicht erst waehlbar.
                                            const takenElsewhere = !isSelected && usedIds.has(s.id);

                                            return (
                                                <div
                                                    key={s.id}
                                                    title={serviceTooltip(s, blockInfo?.reason, takenElsewhere, canSeeFinancials)}
                                                    onClick={() => {
                                                        if (takenElsewhere) return;
                                                        if (!isBlocked) {
                                                            chooseService(index, s.id);
                                                        } else {
                                                            // Allow forcing
                                                            confirmBlocked(blockInfo, () => chooseService(index, s.id));
                                                        }
                                                    }}
                                                    className={`p-3 rounded-xl border-2 transition-all flex items-start justify-between gap-3 group
                                                        ${takenElsewhere
                                                            ? 'bg-slate-50 border-slate-100 opacity-50 cursor-not-allowed'
                                                            : (isBlocked
                                                                ? 'bg-amber-50/50 border-amber-100 cursor-pointer opacity-100 hover:border-amber-300 hover:bg-amber-50 hover:shadow-sm' // Changed style for blocked
                                                                : (isSelected
                                                                    ? 'border-blue-500 bg-blue-50 cursor-pointer shadow-sm'
                                                                    : 'border-slate-100 hover:border-blue-300 hover:bg-blue-50/40 hover:shadow-sm cursor-pointer'
                                                                )
                                                            )
                                                        }
                                                    `}
                                                >
                                                    <div className="flex-1 min-w-0">
                                                        {/*
                                                          * Der volle LV-Titel steht hier, nicht mehr abgeschnitten:
                                                          * zwei Positionen desselben Loses unterscheiden sich oft
                                                          * erst im hinteren Teil des Textes ("... Los 1" / "... Los 2").
                                                          */}
                                                        <div className={`font-bold text-sm leading-snug break-words ${isSelected ? 'text-blue-800' : (isBlocked ? 'text-slate-500' : 'text-slate-700')}`}>
                                                            <span className="font-black">{s.lvPosition}</span> {s.title}
                                                        </div>
                                                        <div className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-2">
                                                            <span>Ist: <b>{s.quantityUsed}</b> {isNepService(s) ? <>{s.unit} · <span className="font-black text-amber-800">{NEP_BADGE}</span></> : `/ ${s.quantityContracted} ${s.unit}`}</span>
                                                            {s.clientLvPosition && s.clientLvPosition !== s.lvPosition && (
                                                                <span className="bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded text-[10px] font-bold">
                                                                    Kunde: {s.clientLvPosition}
                                                                </span>
                                                            )}
                                                            {!!s.unitPrice && canSeeFinancials && <span className="text-slate-400">{formatEuro(s.unitPrice)} / {s.unit}</span>}
                                                            {isDone && !isBlocked && <span className="bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase">Soll erreicht</span>}
                                                            {takenElsewhere && <span className="bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase">Bereits in einer anderen Zeile</span>}
                                                        </div>
                                                        {isBlocked && !takenElsewhere && (
                                                            <div className="mt-2 flex flex-col gap-1">
                                                                <div className="text-[11px] font-bold text-amber-600 bg-amber-50 border border-amber-100 px-2 py-1 rounded flex items-start gap-1.5">
                                                                    <Lock className="w-3 h-3 shrink-0 mt-0.5" />
                                                                    <span className="leading-tight">{blockInfo.reason}</span>
                                                                </div>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        confirmBlocked(blockInfo, () => chooseService(index, s.id));
                                                                    }}
                                                                    className="text-[11px] font-bold text-blue-600 hover:text-blue-800 hover:underline self-start flex items-center gap-1 ml-1"
                                                                >
                                                                    <CheckCircle2 className="w-3 h-3" />
                                                                    Trotzdem buchen (Sperre umgehen)
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 ${isSelected ? 'border-blue-600 bg-blue-600 text-white' : (isBlocked ? 'border-amber-200 text-amber-400' : 'border-slate-300 text-transparent')}`}>
                                                        {isSelected && <CheckCircle2 className="w-3 h-3" />}
                                                        {isBlocked && !isSelected && <Lock className="w-3 h-3" />}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/*
                                  * MENGENFELD: bei einer einzelnen Zeile nur fuer Stundenpositionen
                                  * (wie bisher), beim Aufteilen IMMER — sonst liesse sich eine
                                  * Stueckposition nicht auf zwei Lose verteilen.
                                  */}
                                {ev.showAmount && (
                                    <div className="bg-blue-50 p-4 rounded-xl border border-blue-200 animate-slide-up">
                                        <div className="flex items-center gap-2 mb-3 text-blue-900 font-bold text-sm">
                                            <Clock className="w-4 h-4 text-blue-600"/>
                                            {ev.hourly ? 'Zeitaufwand erfassen (Pflichtfeld)' : 'Menge erfassen (Pflichtfeld)'}
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="relative flex-1">
                                                {/*
                                                  * type="text" + inputMode="decimal", NICHT type="number":
                                                  * ein Zahlenfeld verwirft die Zwischenstufe "2." und
                                                  * loescht damit still die halb getippte Eingabe. Der
                                                  * Rohtext bleibt im State (siehe serviceUnits).
                                                  */}
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={row.amount}
                                                    onChange={(e) => setAmount(index, e.target.value)}
                                                    className={`w-full p-3 pl-4 pr-24 border-2 rounded-xl text-lg font-black outline-none transition-all ${ev.value <= 0 ? 'border-red-300 bg-red-50 text-red-700 focus:ring-red-100' : 'border-blue-300 bg-white text-blue-800 focus:ring-blue-100 focus:ring-4'}`}
                                                    placeholder="0.00"
                                                    autoFocus={index === 0}
                                                />
                                                <span className="absolute right-4 top-1/2 -translate-y-1/2 font-bold text-slate-400">{ev.unitLabel}</span>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-[11px] font-bold text-slate-400 uppercase">Vorschau</div>
                                                <div className="text-xs font-bold text-blue-600">
                                                    {(rowService?.quantityUsed || 0) + ev.value} {ev.hourly ? 'h' : ev.unitLabel} gesamt
                                                </div>
                                            </div>
                                        </div>
                                        {ev.minHours !== null && (
                                            <p className="text-[11px] text-blue-700 font-bold mt-2 flex items-center gap-1">
                                                <Info className="w-3 h-3" /> Mindestens {formatHoursLabel(ev.minHours)} Std. ansetzen.
                                            </p>
                                        )}
                                        {ev.belowMin && (
                                            <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-2.5 flex items-start gap-2">
                                                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                                                <p className="text-[11px] text-amber-800 font-bold leading-snug">
                                                    Weniger als die vereinbarten {formatHoursLabel(ev.minHours as number)} Std. — buchen ist
                                                    möglich, bitte nur bewusst unterschreiten.
                                                </p>
                                            </div>
                                        )}
                                        {ev.value <= 0 && (
                                            <p className="text-[11px] text-red-600 font-bold mt-2 flex items-center gap-1">
                                                <AlertTriangle className="w-3 h-3" />
                                                {ev.hourly ? (texts.hoursMissing || DEFAULT_HOURS_MISSING) : 'Bitte geben Sie die Menge an.'}
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/*
                                  * PAUSCHALE: kein Mengenfeld (evaluateBillingRows blendet es
                                  * aus), stattdessen die Auskunft, was die Buchung bewirkt —
                                  * sonst sucht der Anwender das Feld oder haelt den Monat fuer
                                  * gebucht.
                                  */}
                                {ev.flatProof && (
                                    <div className="bg-indigo-50 p-3 rounded-xl border border-indigo-200 flex items-start gap-2">
                                        <Info className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" />
                                        <p className="text-[11px] text-indigo-800 font-bold leading-snug">{FLAT_PROOF_HINT}</p>
                                    </div>
                                )}

                                {isSplit && ev.serviceId === null && (
                                    <p className="text-[11px] text-red-600 font-bold flex items-center gap-1">
                                        <AlertTriangle className="w-3 h-3" /> Bitte für diese Zeile eine Position wählen.
                                    </p>
                                )}
                            </div>
                        );
                    })}

                    {allowSplit && (
                        <div className="flex items-center justify-between gap-3">
                            <button
                                onClick={addRow}
                                disabled={!hasFreeService}
                                className="text-xs font-bold text-blue-600 hover:text-blue-800 disabled:text-slate-300 disabled:cursor-not-allowed flex items-center gap-1.5 transition-colors"
                            >
                                <Plus className="w-4 h-4" /> Zeile hinzufügen
                            </button>
                            {isSplit && (
                                <div className="text-right">
                                    <div className="text-[11px] font-bold text-slate-400 uppercase">Summe</div>
                                    <div className="text-sm font-black text-slate-700">{formatHoursLabel(total)}</div>
                                </div>
                            )}
                        </div>
                    )}

                    {isSplit && (
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-start gap-2">
                            <Layers className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                            <p className="text-[11px] text-slate-600 leading-snug">
                                Die Begehung wird auf mehrere Lose verteilt: jede Zeile wird als eigene
                                Position gebucht, die Mengen werden je Zeile frei erfasst.
                            </p>
                        </div>
                    )}

                    {!isSplit && (
                        <div className="flex items-center gap-2 pt-2">
                            <input
                                type="checkbox"
                                id="no-billing"
                                checked={gewaehlt && rows[0]?.serviceId === null}
                                onChange={(e) => {
                                    if (e.target.checked) chooseService(0, null);
                                    else {
                                        // Select first non-blocked service
                                        const available = services.find(s => !blockedServices[s.id]);
                                        chooseService(0, available ? available.id : null);
                                    }
                                }}
                                className="rounded text-blue-600 focus:ring-blue-500"
                            />
                            <label htmlFor="no-billing" className="text-sm text-slate-600 cursor-pointer select-none">
                                Vorgang ohne Leistungsbuchung abschließen
                            </label>
                        </div>
                    )}
                </div>

                <div className="p-4 bg-slate-50 border-t flex justify-end gap-3 shrink-0">
                    <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
                    <Button variant="primary" onClick={handleConfirm} disabled={!isValid || !gewaehlt}>
                        Auswahl bestätigen
                    </Button>
                </div>
            </div>
        </div>
    );
};
